import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';

import { NEXTABLE_WAITLIST_AVAILABILITIES_ENDPOINT } from 'reservations/base/constants/endpoints';
import { DEFAULT_QUERY_OPTIONS } from 'reservations/base/constants/queryOptions';
import { restClient } from 'utility/restClient';
import type { RequestError } from 'utility/restClient';

export const EnabledStateEnum = z.enum([
  'VenueClosed',
  'VenueClosing',
  'Enabled',
  'Disabled',
  'Nowait',
  'Off'
]);

// TODO: Introduce WaitlistEstimate for quick follow-up
// export const WaitlistEstimate = z.object({
//   min: z.number().nullish(),
//   max: z.number().nullish()
// });
// export type WaitlistEstimate = z.infer<typeof WaitlistEstimate>;

/**
 * waitCount: The number of parties on the waitlist at the moment
 * enabled: This boolean correlates with enabledState. The only time this is true
 *          is when enabledState is Enabled
 * enabledState: This enum gives us the reason why enabled is true or false
 */
export const WaitlistAvailability = z.object({
  partySize: z.number(),
  waitCount: z.number(),
  enabled: z.boolean(),
  enabledState: EnabledStateEnum,
  // TODO: Uncomment estimate when we do quick follow-up for it
  // estimate: WaitlistEstimate,
  generatedAtUtc: z.string()
});
export type WaitlistAvailability = z.infer<typeof WaitlistAvailability>;

async function fetchWaitlistAvailability(apiKey: string, partySize: number) {
  const endpoint = `${NEXTABLE_WAITLIST_AVAILABILITIES_ENDPOINT}?partySize=${partySize}`;
  const response = await restClient.get(endpoint, {
    nextableApiKey: apiKey
  });

  try {
    return WaitlistAvailability.parse(response);
  } catch (err) {
    console.error('fetchWaitlistAvailability: ', err);
    throw err;
  }
}

type UseWaitlistAvailabilityQueryParams = {
  slug: string;
  apiKey: string;
  enabled: boolean;
  partySize: number;
};

export const useWaitlistAvailabilityQuery = ({
  slug,
  apiKey,
  partySize,
  enabled
}: UseWaitlistAvailabilityQueryParams) =>
  useQuery<WaitlistAvailability, RequestError>({
    ...DEFAULT_QUERY_OPTIONS,
    queryKey: ['waitlist', 'availability', slug, partySize],
    queryFn: () => fetchWaitlistAvailability(apiKey, partySize),
    enabled
  });
